// src/components/controller/IntegratedDisplayView.js
import { useState, useEffect, useCallback } from 'react';
import { ThemeProvider } from 'baseui';
import { Button, SIZE, SHAPE } from 'baseui/button';
import { useAd } from '../../contexts/AdContext';
import { useLocale } from '../../contexts/LocaleContext';
import { useTheme } from '../../contexts/ThemeContext';
import { LightTheme, DarkTheme } from 'baseui';

import { palettes } from '../../constants/display/palettes';
import {
  IntegratedViewContainer,
  IntegratedViewContent,
  LandscapeControls,
  PortraitControlsFloatingBar,
  BackToControlsButton,
} from '../../styles/controllerPageStyles';
import {
  ControlsIcon,
  PlayIcon,
  PauseIcon,
  PrevIcon,
  NextIcon,
  LandscapePlayIcon,
  LandscapePauseIcon,
  LandscapePrevIcon,
  LandscapeNextIcon,
  LandscapeResetIcon,
  LandscapeResetAllIcon,
} from '../../styles/iconStyles';
import {
  createModalOverrides,
} from '../../styles/displayModalStyles';
import DisplayHeader from '../display/DisplayHeader';
import DisplayStructure from '../display/DisplayStructure';
import { DisplaySettingsModal } from '../display/DisplaySettingsModal';
import GlobalAdArea from '../common/GlobalAdArea';

const IntegratedDisplayView = (props) => {
  const { structureData, tournamentDetails, remainingTime, rowIndex, currentTime, isPortrait, timerState, rows, appConfig, tournamentActions, setViewMode, displaySettings, displayActions, tournamentName } = props;
  const settings = displaySettings;
  const currentThemeName = settings.currentThemeName;
  const settingsActions = displayActions;
  const { t, locale } = useLocale();
  const { isAdVisible } = useAd();
  const { themeMode } = useTheme();

  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);

  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getButtonSize = useCallback(() => {
    if (windowHeight >= 800) return SIZE.large;
    if (windowHeight >= 480) return SIZE.default;
    return SIZE.mini;
  }, [windowHeight]);

  const getIconSize = useCallback(() => {
    if (windowHeight >= 800) return 32;
    if (windowHeight >= 480) return 24;
    return 18;
  }, [windowHeight]);

  const buttonSize = getButtonSize();
  const iconSize = getIconSize();

  
  
  const palette = palettes[currentThemeName] || palettes.midnight;
const baseTheme = themeMode === 'dark' ? DarkTheme : LightTheme;
  const customTheme = {
    ...baseTheme,
    colors: {
      ...baseTheme.colors,
      ...palette,
    },
  };

  useEffect(() => {
    const handleFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFsChange);
    return () => document.removeEventListener('fullscreenchange', handleFsChange);
  }, []);

  const toggleFullScreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
    }
  }, []);

  return (
    <ThemeProvider theme={customTheme}>
      <IntegratedViewContainer $isAdVisible={isAdVisible} $palette={palette}>
        <GlobalAdArea />
        <IntegratedViewContent $isAdVisible={isAdVisible}>
          <DisplayHeader
            palette={palette}
            structure={structureData}
            tournamentDetails={tournamentDetails}
            tournamentName={tournamentName}
            remainingTime={remainingTime}
            rowIndex={rowIndex}
            currentTime={currentTime}
            isPortrait={isPortrait}
            isFullscreen={isFullscreen}
            showQrCode={settings.showQrCode}
            toggleFullScreen={toggleFullScreen}
            onOpenSettings={() => { settingsActions.getAudioContext(); setIsSettingsModalOpen(true);}}
            t={t}
            iconSize={iconSize}
          >
            {!isPortrait && (
              <LandscapeControls>
                <Button 
                onClick={timerState === 'play' ? tournamentActions.pauseTimer : tournamentActions.startTimer}
                disabled={timerState === 'stop'}
                size={buttonSize}
                shape={SHAPE.square}
              >
                {timerState === 'play' ? <LandscapePauseIcon $size={iconSize}/> : <LandscapePlayIcon $size={iconSize}/>}
              </Button>
              <Button onClick={tournamentActions.prevLevel} disabled={rowIndex === 0} size={buttonSize} shape={SHAPE.square}><LandscapePrevIcon $size={iconSize}/></Button>
              <Button onClick={tournamentActions.nextLevel} disabled={rowIndex >= rows.length - 1} size={buttonSize} shape={SHAPE.square}><LandscapeNextIcon $size={iconSize}/></Button>
              <Button onClick={tournamentActions.resetToRowStart} size={buttonSize} shape={SHAPE.square}><LandscapeResetIcon $size={iconSize}/></Button>
              <Button onClick={tournamentActions.resetTimer} size={buttonSize} shape={SHAPE.square}><LandscapeResetAllIcon $size={iconSize}/></Button>
              </LandscapeControls>
            )}
          </DisplayHeader>
          <DisplayStructure
            palette={palette}
            structure={structureData}
            rowIndex={rowIndex}
            isMobilePortrait={isPortrait}
            t={t}
            appConfig={appConfig}
          />
        </IntegratedViewContent>
        
        {isPortrait && (
          <PortraitControlsFloatingBar>
            <Button onClick={tournamentActions.prevLevel} disabled={rowIndex === 0} overrides={{ BaseButton: { style: { opacity: 0.5, backgroundColor: palette.buttonPrimaryFill, color: palette.buttonPrimaryText } } }} size={buttonSize} shape={SHAPE.square}><PrevIcon $size={iconSize}/></Button>
            <Button onClick={timerState === 'play' ? tournamentActions.pauseTimer : tournamentActions.startTimer} disabled={timerState === 'stop'} overrides={{ BaseButton: { style: { opacity: 0.5, backgroundColor: palette.buttonPrimaryFill, color: palette.buttonPrimaryText } } }} size={buttonSize} shape={SHAPE.square}>
              {timerState === 'play' ? <PauseIcon $size={iconSize}/> : <PlayIcon $size={iconSize}/>}
            </Button>
            <Button onClick={tournamentActions.nextLevel} disabled={rowIndex >= rows.length - 1} overrides={{ BaseButton: { style: { opacity: 0.5, backgroundColor: palette.buttonPrimaryFill, color: palette.buttonPrimaryText } } }} size={buttonSize} shape={SHAPE.square}><NextIcon $size={iconSize}/></Button>
          </PortraitControlsFloatingBar>
        )}

        <BackToControlsButton>
          <Button onClick={() => setViewMode('control')} shape="circle">
            <ControlsIcon $size={iconSize}/>
          </Button>
        </BackToControlsButton>

        <DisplaySettingsModal
          isOpen={isSettingsModalOpen}
          onClose={() => setIsSettingsModalOpen(false)}
          palette={palette}
          t={t}
          locale={locale}
          isMobile={isPortrait}
          isPortrait={isPortrait}
          settings={settings}
          currentThemeName={currentThemeName}
          structure={structureData}
          actions={settingsActions}
          overrides={createModalOverrides('auto', palette)}
        />
      </IntegratedViewContainer>
    </ThemeProvider>
  );
};

export default IntegratedDisplayView;
